import React, { ReactNode } from 'react';

export interface DataTableColumn<T = any> {
  key: string;
  label: string;
  render?: (row: T) => ReactNode;
}

interface DataTableProps<T = any> {
  columns: DataTableColumn<T>[];
  data: T[];
  loading?: boolean;
  emptyMessage?: string;
  onRowClick?: (id: string | number) => void;
  onEdit?: (id: string | number) => void;
  onDelete?: (id: string | number) => void;
  idKey?: string;
}

function DataTable<T extends Record<string, any>>({
  columns,
  data,
  loading = false,
  emptyMessage = 'No data',
  onRowClick,
  onEdit,
  onDelete,
  idKey = 'id',
}: DataTableProps<T>) {
  const hasActions = !!(onEdit || onDelete);

  const getCellValue = (row: T, col: DataTableColumn<T>) => {
    if (col.render) return col.render(row);
    const v = row[col.key];
    return v != null ? String(v) : '—';
  };

  const getId = (row: T): string | number => {
    const id = row[idKey];
    return id != null ? id : (row as any).id;
  };

  return (
    <div className="dashboard-card rounded-2xl overflow-hidden">
      <div className="flex-auto p-4">
        <div className="overflow-x-auto">
          <table className="table-auto w-full align-middle mb-0">
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="px-4 py-3 text-left align-middle text-[10px] font-bold uppercase tracking-wider text-slate-500 opacity-80 dark:opacity-90"
                >
                  {col.label}
                </th>
              ))}
              {hasActions && (
                <th className="px-4 py-3 text-left align-middle text-[10px] font-bold uppercase tracking-wider text-slate-500 opacity-80 dark:opacity-90">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-600/50">
            {loading ? (
              <tr>
                <td
                  colSpan={columns.length + (hasActions ? 1 : 0)}
                  className="px-4 py-8 text-center align-middle text-xs text-slate-500"
                >
                  Loading…
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (hasActions ? 1 : 0)}
                  className="px-4 py-8 text-center align-middle text-xs text-slate-500"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((row) => {
                const id = getId(row);
                return (
                  <tr
                    key={String(id)}
                    className={`align-middle transition-colors even:bg-slate-50 dark:even:bg-white/5 ${onRowClick ? 'hover:bg-slate-100 dark:hover:bg-white/10 cursor-pointer' : ''}`}
                    onClick={() => onRowClick?.(id)}
                  >
                    {columns.map((col) => (
                      <td key={col.key} className="px-4 py-3 whitespace-nowrap align-middle">
                        <div className="text-sm text-slate-700 dark:text-slate-200">{getCellValue(row, col)}</div>
                      </td>
                    ))}
                    {hasActions && (
                      <td className="px-4 py-3 whitespace-nowrap align-middle">
                        <div
                          className="flex items-center gap-3"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {onEdit && (
                            <button
                              onClick={() => onEdit(id)}
                              className="text-slate-500 font-bold text-xs hover:text-slate-700 dark:hover:text-slate-200"
                              title="Edit user"
                            >
                              Edit
                            </button>
                          )}
                          {onDelete && (
                            <button
                              onClick={() => onDelete(id)}
                              className="text-slate-500 font-bold text-xs hover:text-danger"
                              title="Delete"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default DataTable;
